import {
  Autocomplete,
  Box,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Shop } from "../../../types/shop";
import { FormDialog } from "../../../components/common/FormDialog/FormDialog";
import { cleanPhoneNumber } from "../../../utils/shopUtils";
import LocationInput from "./LocationInput";

interface ShopFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Omit<Shop, "id" | "createdAt" | "updatedAt">) => void;
  isLoading: boolean;
  initialValues: Shop | null;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .max(100, "Name must be at most 100 characters"),
  address: Yup.string()
    .required("Address is required")
    .max(200, "Address must be at most 200 characters"),
  email: Yup.string()
    .email("Invalid email address")
    .max(100, "Email must be at most 100 characters"),
  phoneNumber: Yup.string()
    .matches(
      /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
      "Invalid phone number"
    )
    .max(20, "Phone number must be at most 20 characters"),
  description: Yup.string().max(
    500,
    "Description must be at most 500 characters"
  ),
  workingHours: Yup.string().max(
    100,
    "Working hours must be at most 100 characters"
  ),
  latitude: Yup.number()
    .required("Latitude is required")
    .min(-90, "Latitude must be between -90 and 90")
    .max(90, "Latitude must be between -90 and 90"),
  longitude: Yup.number()
    .required("Longitude is required")
    .min(-180, "Longitude must be between -180 and 180")
    .max(180, "Longitude must be between -180 and 180"),
  isActive: Yup.boolean().required("Status is required"),
});

const ShopFormModal: React.FC<ShopFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  initialValues,
}) => {
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  }>({
    latitude: initialValues?.latitude ?? 41.8781,
    longitude: initialValues?.longitude ?? -87.6298,
  });

  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      email: "",
      phoneNumber: "",
      description: "",
      workingHours: "",
      latitude: location.latitude,
      longitude: location.longitude,
      isActive: true,
      preference: 0, // Default to Trusted (0)
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  useEffect(() => {
    if (initialValues) {
      // Treat shops with negative IDs (Google shops) as new shops
      const isNewShop = !initialValues.id || initialValues.id < 0;

      formik.setValues({
        name: initialValues.name,
        address: initialValues.address,
        email: initialValues.email ?? "",
        phoneNumber: initialValues.phoneNumber ? cleanPhoneNumber(initialValues.phoneNumber) ?? "" : "",
        description: initialValues.description ?? "",
        workingHours: initialValues.workingHours ?? "",
        latitude: initialValues.latitude,
        longitude: initialValues.longitude,
        isActive: initialValues.isActive ?? true,
        preference: initialValues.preference ?? 0, // Default to Trusted if not set
      });
      setLocation({
        latitude: initialValues.latitude,
        longitude: initialValues.longitude,
      });
    } else {
      formik.resetForm();
      setLocation({
        latitude: 41.8781,
        longitude: -87.6298,
      });
    }
  }, [initialValues]);

  const handleLocationChange = (lat: number, lng: number, address?: string) => {
    setLocation({ latitude: lat, longitude: lng });
    formik.setFieldValue("latitude", lat);
    formik.setFieldValue("longitude", lng);
    if (address) {
      formik.setFieldValue("address", address);
    }
  };

  const handlePlaceSelect = async (
    prediction: google.maps.places.AutocompletePrediction | null
  ) => {
    if (!prediction || !placesService.current || !window.google?.maps?.places) {
      console.error("Places service is not initialized");
      return;
    }

    try {
      placesService.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ["geometry", "formatted_address"],
        },
        (place, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            place?.geometry?.location
          ) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            handleLocationChange(lat, lng, place.formatted_address);
          }
        }
      );
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  const handleAddressInputChange = async (_: any, newValue: string) => {
    formik.setFieldValue("address", newValue);

    if (!newValue || !autocompleteService.current) {
      setPredictions([]);
      return;
    }

    try {
      const response = await autocompleteService.current.getPlacePredictions({
        input: newValue,
      });
      setPredictions(response.predictions);
    } catch (error) {
      console.error("Error fetching predictions:", error);
      setPredictions([]);
    }
  };

  const initializeServices = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    autocompleteService.current = new google.maps.places.AutocompleteService();
    placesService.current = new google.maps.places.PlacesService(map);
  }, []);

  return (
    <FormDialog
      open={open}
      title={initialValues ? "Edit Shop" : "Create Shop"}
      onClose={() => {
        formik.resetForm();
        onClose();
      }}
      onSubmit={formik.handleSubmit}
      isLoading={isLoading}
      submitLabel={initialValues ? "Save" : "Create"}
    >
      <Box
        component="form"
        noValidate
        sx={{
          mt: 1,
          border: 2,
          borderColor:
            formik.values.preference === 1
              ? "error.main"
              : formik.values.preference === 0
              ? "#FFA726"
              : "divider",
          borderRadius: 1,
          p: 2,
          transition: "border-color 0.2s ease-in-out",
        }}
      >
        <LocationInput
          latitude={formik.values.latitude}
          longitude={formik.values.longitude}
          onLocationChange={handleLocationChange}
          onMapLoad={initializeServices}
        />
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Shop Preference *
          </Typography>
          <ToggleButtonGroup
            value={formik.values.preference}
            exclusive
            onChange={(_, value) => {
              if (value !== null) {
                formik.setFieldValue("preference", value);
              }
            }}
            aria-label="shop preference"
            fullWidth
          >
            <ToggleButton
              value={0}
              aria-label="trusted"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "#FFA726",
                  color: "black",
                  "&:hover": {
                    backgroundColor: "#FB8C00",
                  },
                },
              }}
            >
              Trusted
            </ToggleButton>
            <ToggleButton
              value={1}
              aria-label="do not use"
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "error.main",
                  color: "error.contrastText",
                  "&:hover": {
                    backgroundColor: "error.dark",
                  },
                },
              }}
            >
              DNU (Do Not Use)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <TextField
          margin="normal"
          required
          fullWidth
          id="name"
          name="name"
          label="Name"
          value={formik.values.name}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          size="small"
        />
        <Autocomplete
          freeSolo
          options={predictions}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.description
          }
          value={formik.values.address}
          onChange={(_, newValue) => {
            if (typeof newValue === "string") {
              formik.setFieldValue("address", newValue);
            } else if (newValue) {
              handlePlaceSelect(newValue);
            }
          }}
          onInputChange={handleAddressInputChange}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="normal"
              required
              fullWidth
              id="address"
              name="address"
              label="Address"
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
              size="small"
            />
          )}
        />
        <TextField
          margin="normal"
          fullWidth
          id="email"
          name="email"
          label="Email"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          size="small"
        />
        <TextField
          margin="normal"
          fullWidth
          id="phoneNumber"
          name="phoneNumber"
          label="Phone Number"
          value={formik.values.phoneNumber}
          onChange={formik.handleChange}
          error={
            formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
          }
          helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
          size="small"
        />
        <TextField
          margin="normal"
          fullWidth
          id="description"
          name="description"
          label="Description"
          multiline
          rows={4}
          value={formik.values.description}
          onChange={formik.handleChange}
          error={
            formik.touched.description && Boolean(formik.errors.description)
          }
          helperText={formik.touched.description && formik.errors.description}
          size="small"
        />
        <TextField
          margin="normal"
          fullWidth
          id="workingHours"
          name="workingHours"
          label="Working Hours"
          value={formik.values.workingHours}
          onChange={formik.handleChange}
          error={
            formik.touched.workingHours && Boolean(formik.errors.workingHours)
          }
          helperText={formik.touched.workingHours && formik.errors.workingHours}
          size="small"
        />
      </Box>
    </FormDialog>
  );
};

export default ShopFormModal;
